import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader, Empty, Badge } from "@/components/common";
import Modal from "@/components/Modal";
import { Plus, Pencil, Trash2, Loader2, UserPlus } from "lucide-react";

const EMPTY = { email: "", name: "", role: "recruiter", password: "" };
const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "recruiter", label: "Recruiter" },
  { value: "research_analyst", label: "Research Analyst" },
];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { setError(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(""); setModalOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setError(""); setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError("");
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      setModalOpen(false); await fetchUsers();
    } catch (e2) { setError(formatErr(e2)); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); await fetchUsers(); }
    catch (e) { alert(formatErr(e)); }
  };

  const roleBadge = (role) => {
    const map = { admin: "danger", recruiter: "brand", research_analyst: "warning" };
    return <Badge variant={map[role] || "default"}>{role.replace("_", " ")}</Badge>;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        testId="users-header"
        title="Users"
        subtitle="Manage who can access Neoventra ATS and what they can do."
        action={
          <button className="btn-primary" onClick={openCreate} data-testid="create-user-button">
            <UserPlus size={16} /> New User
          </button>
        }
      />

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <div className="card-base">
        {loading ? <div className="p-8 text-gray-500 text-sm">Loading users…</div>
          : users.length === 0 ? <div className="p-6"><Empty title="No users yet" /></div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Created</Th><Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50 group" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 font-medium text-gray-900">{u.name}</td>
                    <td className="py-3 px-4 text-gray-700">{u.email}</td>
                    <td className="py-3 px-4">{roleBadge(u.role)}</td>
                    <td className="py-3 px-4 text-gray-500 font-mono text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end gap-1">
                        <button onClick={() => openEdit(u)} className="btn-ghost" data-testid={`edit-user-${u.id}`}><Pencil size={14} /></button>
                        <button onClick={() => remove(u.id)} className="btn-ghost text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit User" : "New User"} testId="user-modal">
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="user-name" />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required disabled={!!editing} testId="user-email" />
            <div>
              <label className="label-base">Role *</label>
              <select className="input-base" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <Field
              label={editing ? "New Password (leave blank to keep)" : "Password *"}
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              required={!editing}
              testId="user-password"
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="user-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : (editing ? "Save changes" : "Create user")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4 ${className}`}>{children}</th>;
}
function Field({ label, value, onChange, required, type = "text", disabled, testId }) {
  return (
    <label className="block">
      <span className="label-base">{label}</span>
      <input type={type} required={required} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-base disabled:bg-gray-100" data-testid={testId} />
    </label>
  );
}
